// src/utils/impactScorecard.js
// Pure, Firebase-free helpers for the IMPACT Scorecard (2026 fillable PDF).
//   computeScorecardMetrics — turns the deal list into the scorecard's numbers.
//   buildScorecardFields    — maps scorecard state to the PDF's exact field names.
import { parseLocalDate, isOpen, notDeleted, ownedBy } from "./statusSheetData";

const isClosedWon = (d) => String(d?.dealStage || "").toLowerCase() === "closed";

// A closed deal's effective close date: the stamped closedDate, else the
// contractual closingdate. parseLocalDate handles both ISO timestamps and
// "YYYY-MM-DD" calendar strings.
const closeDateOf = (d) => parseLocalDate(d?.closedDate || d?.closingdate);

const inMonth = (dt, year, month) =>
  !!dt && dt.getFullYear() === year && dt.getMonth() === month;

// Numbers for the scorecard.
//   "Last month"  = the calendar month before asOf — units/volume/gross income
//                   of deals CLOSED then.
//   "This month"  = OPEN deals whose closing date lands in asOf's month —
//                   the sheet's "Contracts (Closing this month) Locked Down MTD".
//   "Next month"  = OPEN deals closing the month after asOf — the sheet's
//                   "Contracts (Closing next month) Locked Down".
// agentId omitted/undefined → team-wide (ownedBy returns true for everyone).
export function computeScorecardMetrics(deals, { agentId, asOf = new Date() } = {}) {
  const base = new Date(asOf.getFullYear(), asOf.getMonth(), 1);
  const prev = new Date(base.getFullYear(), base.getMonth() - 1, 1);
  const next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
  const list = Array.isArray(deals) ? deals : [];

  const closedLastMonth = list.filter(
    (d) =>
      isClosedWon(d) &&
      notDeleted(d) &&
      ownedBy(d, agentId) &&
      inMonth(closeDateOf(d), prev.getFullYear(), prev.getMonth()),
  );

  const unitsLastMonth = closedLastMonth.length;
  const volumeLastMonth = closedLastMonth.reduce(
    (sum, d) => sum + Number(d.offerPrice || 0),
    0,
  );
  // The 2026 sheet asks for GROSS income; the agent's commission amount is the
  // closest thing the deal record carries (every number is editable in the tab).
  const grossIncomeLastMonth = closedLastMonth.reduce(
    (sum, d) => sum + Number(d.agentCommissionamount || 0),
    0,
  );

  const openClosingIn = (year, month) =>
    list.filter(
      (d) =>
        isOpen(d) &&
        notDeleted(d) &&
        ownedBy(d, agentId) &&
        d.closingdate &&
        inMonth(parseLocalDate(d.closingdate), year, month),
    ).length;

  const contractsThisMonth = openClosingIn(base.getFullYear(), base.getMonth());
  const contractsNextMonth = openClosingIn(next.getFullYear(), next.getMonth());

  return {
    unitsLastMonth,
    volumeLastMonth,
    grossIncomeLastMonth,
    contractsThisMonth,
    contractsNextMonth,
  };
}

// The printed "Current Quarter Goals #3" gets two lines on the sheet ("3 1" and
// "3 2"). Split long text at a word boundary so nothing is cut off in print.
const LINE_MAX = 80;
export function splitTwoLines(text, max = LINE_MAX) {
  const s = String(text == null ? "" : text).trim();
  if (s.length <= max) return [s, ""];
  let cut = s.lastIndexOf(" ", max);
  if (cut <= 0) cut = max;
  return [s.slice(0, cut).trim(), s.slice(cut).trim()];
}

// Map scorecard state to the 2026 PDF's AcroForm field names. Every value is
// coerced to a string so unset fields clear (never write `undefined`).
//
// Field-name legend (the PDF generator numbered repeated "1." labels):
//   Current Quarter Goals →  "1", "2", "3 1" + "3 2" (goal #3 wraps to 2 lines)
//   Top 4 Goals for Year  →  "1_2", "2_2", "3", "4"
//   Top 2 Personal Goals  →  "1_3", "2_3"
//   Scorecard column      →  "MY SCORECARD" (units), "undefined" (volume),
//                            gross income, savings, then two actual/required
//                            contract pairs: MTD ("Contracts Locked Down MTD" /
//                            "undefined_2") and Next Month ("Contracts Locked
//                            Down Next Month" / "undefined_3").
// Verified against the rendered PDF (widget geometry + pdftotext) 2026-07-15 —
// NOTE: the pre-2026 mapping had the three goal blocks scrambled; this one is
// confirmed by a filled visual proof.
export function buildScorecardFields(v = {}) {
  const s = (x) => (x == null ? "" : String(x));
  const year = v.yearGoals || [];
  const personal = v.personalGoals || [];
  const quarter = v.quarterGoals || [];
  const [q3a, q3b] = splitTwoLines(quarter[2]);
  return {
    Name: s(v.name),
    "Coaching Call Date": s(v.coachingCallDate),
    "My Why": s(v.why),
    "MY SCORECARD": s(v.units),
    undefined: s(v.volume),
    "Gross Income Last Month 2nd call": s(v.grossIncome),
    "Savings Last Month 2nd call": s(v.savings),
    "Contracts Locked Down MTD": s(v.contractsMtdActual),
    undefined_2: s(v.contractsMtdRequired),
    "Contracts Locked Down Next Month": s(v.contractsNextActual),
    undefined_3: s(v.contractsNextRequired),
    1: s(quarter[0]),
    2: s(quarter[1]),
    "3 1": s(q3a),
    "3 2": s(q3b),
    "1_2": s(year[0]),
    "2_2": s(year[1]),
    3: s(year[2]),
    4: s(year[3]),
    "1_3": s(personal[0]),
    "2_3": s(personal[1]),
    "Accountability Choice": s(v.accountability),
  };
}
